import React from 'react';
import { 
  Brain, 
  Volume2, 
  Mic, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Users,
  Clock
} from 'lucide-react';
import MetricCard from '../MetricCard';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

const ServiceOverviewStats = ({ stats, loading }) => {
  const serviceTypes = [
    { name: 'LLM', icon: Brain, color: 'bg-blue-500' },
    { name: 'TTS', icon: Volume2, color: 'bg-green-500' },
    { name: 'STT', icon: Mic, color: 'bg-purple-500' },
  ];

  const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Нет данных для отображения</p>
      </div>
    );
  }

  // Prepare data for charts
  const usageTrendData = stats.usageTrend || [];
  const serviceDistributionData = stats.serviceDistribution || [];
  const costTrendData = stats.costTrendData || [];

  const totalRequests = stats.totalRequests || 0;
  const totalCost = stats.totalCost || 0;
  const activeUsers = stats.activeUsers || 0;
  const avgResponseTime = stats.avgResponseTime || 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('ru-RU').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего запросов</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatNumber(totalRequests)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.requestsTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.requestsTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.requestsTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общие затраты</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(totalCost)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.costTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-green-500" />
            )}
            <span className={stats.costTrend >= 0 ? 'text-red-600' : 'text-green-600'}>
              {Math.abs(stats.costTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Активные пользователи</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatNumber(activeUsers)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.usersTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.usersTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.usersTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Среднее время ответа</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{avgResponseTime}мс</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.responseTimeTrend <= 0 ? (
              <TrendingDown className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingUp className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.responseTimeTrend <= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.responseTimeTrend)}% за период
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Trend Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Динамика использования</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={usageTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
                formatter={(value) => [formatNumber(value), 'Запросов']}
              />
              <Line 
                type="monotone" 
                dataKey="requests" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Service Distribution Pie Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Распределение по сервисам</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={serviceDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {serviceDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [formatNumber(value), 'Запросов']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Динамика затрат</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={costTrendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
              formatter={(value) => [formatCurrency(value), 'Затраты']}
            />
            <Bar dataKey="cost" fill="#10B981" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Service Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {serviceTypes.map((service) => {
          const serviceData = stats.services?.[service.name.toLowerCase()] || {};
          return (
            <div key={service.name} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 ${service.color} rounded-lg`}>
                  <service.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">{service.name}</h3>
              </div>
              <div className="space-y-3">
                <MetricCard
                  label="Запросов"
                  value={formatNumber(serviceData.requests || 0)}
                  description="Общее количество запросов к сервису"
                />
                <MetricCard
                  label="Затраты"
                  value={formatCurrency(serviceData.cost || 0)}
                  description="Общие затраты на сервис"
                />
                <MetricCard
                  label="Среднее время"
                  value={`${serviceData.avgResponseTime || 0}мс`}
                  description="Среднее время ответа сервиса"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceOverviewStats;
